// The signed-in console: header, role-aware navigation and the page.
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useState, type ReactNode } from "react";
import { NavLink, Outlet } from "react-router";
import { api, session } from "../api";
import { useSession } from "../auth/useSession";
import { canOpen, navFor, roleLabel, type NavItem, type StaffRole } from "../nav";
import { SignInPage } from "../pages/SignInPage";
import { errorText, unwrap } from "../problem";

const RoleContext = createContext<StaffRole | null>(null);

/** The caller's current role (from `GET /me`, else the one signed in with). */
export function useRole(): StaffRole {
  const role = useContext(RoleContext);
  if (role === null) throw new Error("useRole outside the console shell");
  return role;
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => unwrap(await api.GET("/me")),
    // A role change on the server shows up within a minute.
    refetchInterval: 60_000,
  });
}

export function RequireSession({ children }: { children: ReactNode }) {
  const stored = useSession();
  return stored ? children : <SignInPage />;
}

export function Shell() {
  const stored = useSession();
  const me = useMe();
  const [signingOut, setSigningOut] = useState(false);
  if (!stored) return null;
  const staff = me.data ?? stored.staff;
  const role = staff.role;
  const ends = new Date(stored.sessionExpiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  async function signOut() {
    setSigningOut(true);
    try {
      await session.signOut();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <RoleContext.Provider value={role}>
      <div className="shell">
        <header className="topbar">
          <span className="brand">
            ten20 <span className="brand-sub">admin</span>
          </span>
          <span className="who">
            <span title={staff.email}>{staff.name || staff.email}</span>
            <span className={`role role-${role}`}>{roleLabel(role)}</span>
            <span className="muted">Session ends {ends}</span>
            <button type="button" className="button-quiet" onClick={() => void signOut()} disabled={signingOut}>
              Sign out
            </button>
          </span>
        </header>
        <nav className="sidebar" aria-label="Sections">
          {navFor(role).map(({ group, items }) => (
            <section key={group}>
              <h2>{group}</h2>
              <ul>
                {items.map((item) => (
                  <li key={item.path}>
                    <NavLink to={item.path} end={item.path === "/"}>
                      {item.label}
                      {item.arrives && <span className="soon">{item.arrives}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </nav>
        <main className="content">
          {me.isError && <p className="error">{errorText(me.error)}</p>}
          <Outlet />
        </main>
      </div>
    </RoleContext.Provider>
  );
}

/** Renders a section only for the roles it lists (the server enforces the same). */
export function RoleGate({ item, children }: { item: NavItem; children: ReactNode }) {
  const role = useRole();
  if (canOpen(role, item)) return children;
  return (
    <>
      <h1>{item.label}</h1>
      <p>
        {roleLabel(role)}s can't open {item.label}.
      </p>
    </>
  );
}
